<template>
  <div class="flex flex-col">

    <p
      class="text-base lg:text-lg px-2 lg:px-3 lg:py-1 text-gray-600 font-medium border-l-4 lg:border-l-8 border-theme-secondary mb-3 lg:mb-6">
      Personal Information</p>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 lg:gap-4">
      <TextControl :max-length="40" ref="last_name" label="Last Name" fieldId="last_name" :required="true"
        v-model="form.last_name" />
      <TextControl :max-length="40" ref="first_name" label="First Name" fieldId="first_name" :required="true"
        v-model="form.first_name" />
      <TextControl :max-length="40" ref="middle_name" label="Middle Name" fieldId="middle_name" :required="false"
        v-model="form.middle_name" />
      <TextControl :max-length="8" ref="account_no" label="Account Number" fieldId="account_no" :required="true"
        v-model="form.account_no" />
      <TextControl :max-length="15" ref="mem_key" label="Member Key" fieldId="mem_key" :required="true"
        v-model="form.mem_key" />
      <ComboBoxControl label="Gender" fieldId="gender" v-model="form.gender" :required="true"
        :options="genderOptions" />
      <ComboBoxControl label="Marital Status" fieldId="marital_status" v-model="form.marital_status" :required="true"
        :options="maritalStatusOptions" />
      <DatePickerControl label="Date of Birth" fieldId="date_of_birth" v-model="form.date_of_birth" :required="true" />
      <TextControl :max-length="40" ref="place_of_birth" label="Place of Birth" fieldId="place_of_birth"
        :required="true" v-model="form.place_of_birth" />
      <ComboBoxControl label="Nationality" fieldId="nationality" v-model="form.nationality" :required="true"
        :options="nationalityOptions" />
      <ComboBoxControl label="Citizenship" fieldId="citizenship" v-model="form.citizenship" :required="true"
        :options="citizenshipOptions" />
      <ComboBoxControl label="Country" fieldId="country" v-model="form.country" :required="true"
        :options="countryOptions" />

      <!-- IF PHILIPPINES -->
      <template v-if="isPhilippines">
        <ComboBoxControl label="Province" fieldId="province" v-model="form.province" :required="true"
          :options="provinceOptions" :disabled="!form.country" />
        <ComboBoxControl label="City" fieldId="city" v-model="form.city" :required="true" :options="cityOptions"
          :disabled="!form.province" :is-loading="cityLoading" />
        <ComboBoxControl label="Barangay" fieldId="brgy" v-model="form.brgy" :required="true" :options="brgyOptions"
          :disabled="!form.city" :is-loading="brgyLoading" />
      </template>

      <!-- IF NOT PHILIPPINES -->
      <template v-else>
        <TextControl :max-length="40" label="Province" fieldId="province" v-model="form.province" :required="true"
          :disabled="!form.country" />
        <TextControl :max-length="40" label="City" fieldId="city" v-model="form.city" :required="true"
          :disabled="!form.province" />
        <TextControl :max-length="40" label="Barangay" fieldId="brgy" v-model="form.brgy" :required="true"
          :disabled="!form.city" />
      </template>

      <TextControl :max-length="40" ref="street" label="Street" fieldId="street" :required="false"
        v-model="form.street" />
      <TextControl :max-length="40" ref="house_no" label="House No." fieldId="house_no" :required="false"
        v-model="form.house_no" />
      <TextControl :max-length="10" ref="zip_code" label="Zip Code" fieldId="zip_code" :required="true"
        v-model="form.zip_code" is_zip is_num />
      <TextControl :max-length="15" ref="tin" label="TIN" fieldId="tin" v-model="form.tin" is_num />
      <TextControl :max-length="20" ref="sss" label="GSIS/SSS No." fieldId="sss" :required="false" v-model="form.sss"
        is_num />
      <TextControl ref="reason" label="Specify the reason why TIN and/or GSIS/SSS is not available" fieldId="zip_code"
        :required="false" v-model="form.reason" />
      </div>

      <p class="text-base lg:text-lg px-2 lg:px-3 lg:py-1 text-gray-600 font-medium border-l-4 lg:border-l-8 border-theme-secondary mb-3 lg:mb-6 mt-6 lg:mt-12">
      Personal Information</p>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 lg:gap-4">
        <TextControl ref="mobile_no" label="Mobile No." fieldId="mobile_no" is_phone :required="true" :need_update="'mobile'" disabled
          v-model="form.mobile_no" @validation-status="updateValidation" />
        <TextControl :max-length="50" ref="email" label="Email Address" fieldId="email" is_email :required="true" :need_update="'email'" disabled
          v-model="form.email" @validation-status="updateValidation" />
      </div>
    

    <p
      class="text-base lg:text-lg px-2 lg:px-3 lg:py-1 text-gray-600 font-medium border-l-4 lg:border-l-8 border-theme-secondary mb-3 lg:mb-6 mt-6 lg:mt-12">
      Additional Information for Non-Filipino Clients</p>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 lg:gap-4">
      <TextControl ref="purpose_of_stay" label="Purpose of Stay" fieldId="purpose_of_stay" :required="false"
        v-model="form.purpose_of_stay" :disabled="form.citizenship == 'FILIPINO'" />
      <TextControl ref="type_visa" label="Type of Visa" fieldId="type_visa" :required="false" v-model="form.type_visa"
        :disabled="form.citizenship == 'FILIPINO'" />
      <TextControl ref="length_stay" label="Length of Stay" fieldId="length_stay" :required="false"
        v-model="form.length_stay" :disabled="form.citizenship == 'FILIPINO'" />
      <TextControl ref="req_visit" label="Frequency of Visit" fieldId="req_visit" :required="false"
        v-model="form.req_visit" :disabled="form.citizenship == 'FILIPINO'" />
      <TextControl ref="specify_purpose" label="Specify Purpose" fieldId="specify_purpose" :required="false"
        v-model="form.specify_purpose" :disabled="form.citizenship == 'FILIPINO'" />
      <TextControl ref="org" label="Affiliated Organization/Company/Groups in the Philippines" fieldId="affiliated_org"
        :required="false" v-model="form.affiliated_org" :disabled="form.citizenship == 'FILIPINO'" />
    </div>

  </div>


  <div class="mt-8 xl:mt-16 flex flex-row gap-2 lg:gap-4 justify-end w-full">
      <ButtonControl theme="bg-primary border-2 border-theme-primary text-white" rounded button_text="Next"
        @click="$emit('next')" :disabled="!isFormComplete" class="w-full lg:w-[20%]" />
  </div>


</template>

<script>
import TextControl from '@/components/controls/TextControl.vue';
import ComboBoxControl from '@/components/controls/ComboBoxControl.vue';
import DatePickerControl from '@/components/controls/DatePickerControl.vue';
import ButtonControl from '@/components/controls/ButtonControl.vue';

import User from '@/services/User';
import mixin from '@/mixin';

export default {
  name: 'v-tab-1',
  inject: ['setLoading'],
  mixins: [mixin],
  data() {
    return {
      validationErrors: {},
      cityOptions: [],
      brgyOptions: [],
      cityLoading: false,
      brgyLoading: false,
      form: {
        last_name: '',
        first_name: '',
        middle_name: '',
        account_no: '',
        mem_key: '',
        gender: '',
        marital_status: '',
        date_of_birth: '',
        place_of_birth: '',
        nationality: '',
        citizenship: '',
        country: '',
        province: '',
        city: '',
        brgy: '',
        street: '',
        house_no: '',
        zip_code: '',
        tin: '',
        sss: '',
        reason: '',
        mobile_no: '',
        email: '',
        purpose_of_stay: '',
        type_visa: '',
        length_stay: '',
        req_visit: '',
        specify_purpose: '',
        affiliated_org: ''
      }
    };
  },
  components: {
    TextControl,
    ComboBoxControl,
    DatePickerControl,
    ButtonControl
  },
  computed: {
    isFormComplete() {
      return (
        this.form.last_name &&
        this.form.first_name &&
        this.form.account_no &&
        this.form.mem_key &&
        this.form.gender &&
        this.form.marital_status &&
        this.form.date_of_birth &&
        this.form.place_of_birth &&
        this.form.nationality &&
        this.form.citizenship &&
        this.form.country &&
        this.form.province &&
        this.form.city &&
        this.form.brgy &&
        this.form.zip_code &&
        this.form.mobile_no &&
        this.form.email
      );
    },
    genderOptions() {
      const lookup = this.$store.state.lookup;
      if (!lookup || !lookup.gender) return [];

      return lookup.gender.map(g => ({
        label: g,
        value: g
      }));
    },
    countryOptions() {
      const lookup = this.$store.state.lookup;
      if (!lookup || !lookup.country) return [];

      return lookup.country.map(c => ({
        label: c,
        value: c
      }));
    },
    maritalStatusOptions() {
      const lookup = this.$store.state.lookup;
      if (!lookup || !lookup.marital_status) return [];

      return lookup.marital_status.map(ms => ({
        label: ms,
        value: ms
      }));
    },
    nationalityOptions() {
      const nationality = this.$store.state.lookup?.nationality;

      if (!Array.isArray(nationality)) return [];

      return nationality.map(item => ({
        label: item.nationality,
        value: item.nationality
      }));
    },
    citizenshipOptions() {
      const nationality = this.$store.state.lookup?.nationality;

      if (!Array.isArray(nationality)) return [];

      return nationality.map(item => ({
        label: item.nationality,
        value: item.nationality
      }));
    },
    provinceOptions() {
      const provinces = this.$store.state.provinces;

      if (!Array.isArray(provinces)) return [];

      return provinces.map(p => ({
        label: p.province_name,
        value: p.province_code
      }));
    },
    isPhilippines() {
      return this.form.country === 'PHILIPPINES';
    },
  },
  mounted() {
    this.restoreFormFromStore();
    this.autoFillFromCredentials();
    this.$store.commit('setFormDataRCBC', this.form);
  },
  watch: {
    form: {
      handler(newForm) {
        this.$store.commit('setFormDataRCBC', newForm);
      },
      deep: true
    },
    'form.province'(newProvince) {
      if (newProvince) {
        this.cityLoading = true;

        // console.log('Fetching cities for province:', newProvince);
        if (this.form.country == 'PHILIPPINES') {
          User.getCity(newProvince)
            .then((response) => {
              this.cityOptions = response.data.data.map(city => ({
                label: city.city_name,
                value: city.city_code
              }));
              this.cityLoading = false;
            })
            .catch((error) => {
              console.error('Error fetching cities:', error);
              this.cityLoading = false;
            });
        }

      }
    },
    'form.city'(newCity) {
      if (newCity) {
        this.brgyLoading = true;
        if (this.form.country == 'PHILIPPINES') {
          User.getBrgy(newCity)
            .then((response) => {
              console.log('Barangays fetched:', response.data.data);
              this.brgyOptions = response.data.data.map(brgy => ({
                label: brgy.brgy_name,
                value: brgy.brgy_code
              }));
              this.brgyLoading = false;
            })
            .catch((error) => {
              console.error('Error fetching barangays:', error);
              this.brgyLoading = false;
            });
        }
      }
    },
  },
  methods: {
    normalizePhone(value) {
      if (!value) return "";

      const digits = String(value).replace(/\D/g, '');
      if (digits.startsWith('+63')) return digits.slice(3);
      if (digits.startsWith('63')) return digits.slice(2);
      if (digits.startsWith('0')) return digits.slice(1);
      return digits;
    },
    restoreFormFromStore() {
      const storeFormData = this.$store.state.formDataRCBC || {};

      Object.keys(this.form).forEach((key) => {
        if (
          Object.prototype.hasOwnProperty.call(storeFormData, key) &&
          storeFormData[key] !== undefined &&
          storeFormData[key] !== null
        ) {
          this.form[key] = storeFormData[key];
        }
      });
    },

    autoFillFromCredentials() {
      const credentials = this.$store.state.credentials || {};

      const mappings = {
        last_name: "last_name",
        first_name: "first_name",
        middle_name: "middle_name",
        suffix: "suffix",
        account_no: "account_no",
        mem_key: "key",
        tin: "tin",
        date_of_birth: "birthdate",
        gender: "gender",
        email: "email"
      };

      Object.entries(mappings).forEach(([formField, credentialField]) => {
        if (!this.form[formField] && credentials[credentialField] != null) {
          let value = credentials[credentialField];

          if (formField === "gender" && typeof value === "string") {
            value = value.toUpperCase();
          }

          this.form[formField] = value;
        }
      });

      if (!this.form.mobile_no && credentials.mobile) {
        this.form.mobile_no = this.normalizePhone(credentials.mobile);
      }
    },

    updateValidation(field, isValid) {
      this.validationErrors[field] = isValid;
    },
  },
};
</script>

<style scoped>
.papsi-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
}

.box {
  width: 4rem !important;
  height: 4rem !important;
  margin-right: 3rem !important;
}

@media (max-width: 1000px) {
  .box {
    width: 2rem !important;
    height: 2rem !important;
    margin-right: 1rem !important;
    font-size: 5rem !important;
  }

  .agree {
    font-size: 1.7rem !important;
  }


}
</style>